/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type ReactNode } from 'react';

// `@compiled/react` is required because `@atlaskit/css` intentionally disallows arbitrary
// descendant selectors, and `@atlaskit/link` does not expose a styling prop for this override.
import { css, jsx } from '@compiled/react';

import Link from '@atlaskit/link/link';
import { token } from '@atlaskit/tokens';

/**
 * Footer links point at long-lived destinations that people return to constantly, so a
 * visited treatment carries no useful signal and just leaves the footer looking patchy.
 *
 * `@atlaskit/link` applies visited colours to every appearance except `inverse`, and
 * exposes no prop to opt out, so we override from a wrapper instead. `.wrapper a:visited`
 * is one point more specific than the component's own `&:visited` rule, which is what
 * makes this stick regardless of stylesheet order.
 *
 * The values below intentionally mirror `subtleAppearance` in `@atlaskit/link` so a
 * visited link is indistinguishable from an unvisited one in every state.
 */
const noVisitedStateStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Overriding a descendant `@atlaskit/link`
	'a:visited, a:visited:hover, a:visited:focus': {
		color: token('color.text.subtle'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Overriding a descendant `@atlaskit/link`
	'a:visited:active': {
		color: token('color.text'),
	},
});

/**
 * __Footer link__
 *
 * A subtle link for use within the site footer. Behaves like `@atlaskit/link`, but
 * without a visited state.
 */
export const FooterLink = ({
	href,
	children,
}: {
	href: string;
	children: ReactNode;
}): JSX.Element => {
	return (
		<span css={noVisitedStateStyles}>
			<Link href={href} target={href.startsWith('http') ? '_blank' : undefined} appearance="subtle">
				{children}
			</Link>
		</span>
	);
};
